<template>
  <v-container class="pa-2 page-layout" fluid>
    <v-row dense>
      <v-col class="py-0">
        <h1 class="page-title">Payments (EOP)</h1>
        <p class="page-subtitle mb-0">
          Manage and upload Explanation of Payment documents
        </p>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-end align-center">
            <v-btn
              v-if="isSuperuser"
              color="primary"
              large
              @click="openUploadDialog"
            >
              <v-icon left>mdi-upload</v-icon>
              Upload EOP
            </v-btn>
          </v-card-title>

          <v-card-text>
            <!-- Statistics -->
            <v-row class="mb-4">
              <v-col cols="12">
                <v-card outlined>
                  <v-card-title class="py-3">
                    <v-icon left>mdi-chart-line</v-icon>
                    Statistics
                  </v-card-title>
                  <v-card-text>
                    <div v-if="stats" class="d-flex justify-space-between">
                      <div class="text-center">
                        <div class="text-h6">{{ stats.overview.activeEOPs }}</div>
                        <div class="caption">Active EOPs</div>
                      </div>
                      <div class="text-center">
                        <div class="text-h6">{{ formatFileSize(stats.overview.totalSize) }}</div>
                        <div class="caption">Total Size</div>
                      </div>
                      <div class="text-center">
                        <div class="text-h6">{{ stats.overview.totalDownloads }}</div>
                        <div class="caption">Downloads</div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Search and Filters -->
            <v-row class="mb-4">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="searchQuery"
                  label="Search EOPs..."
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  dense
                  clearable
                  @input="debouncedSearch"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="statusFilter"
                  :items="statusOptions"
                  label="Status"
                  outlined
                  dense
                  clearable
                  @change="loadEOPs"
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="sortBy"
                  :items="sortOptions"
                  label="Sort By"
                  outlined
                  dense
                  @change="loadEOPs"
                ></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-btn
                  color="primary"
                  @click="loadEOPs"
                  :loading="loading"
                  block
                >
                  Refresh
                </v-btn>
              </v-col>
            </v-row>

            <!-- EOPs Table -->
            <v-data-table
              :headers="headers"
              :items="eops"
              :loading="loading"
              :search="searchQuery"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              multi-sort
              loading-text="Loading EOPs..."
              class="elevation-1"
            >
              <template v-slot:item.PROVIDER_NAME="{ item }">
                <div>
                  <div class="font-weight-medium">{{ item.PROVIDER_NAME }}</div>
                  <div class="caption text--secondary">NPI: {{ item.PROVIDER_NPI }}</div>
                </div>
              </template>

           <template v-slot:item.FILE_SIZE="{ item }">
             <div class="font-weight-medium">{{ formatFileSize(item.FILE_SIZE) }}</div>
           </template>

              <template v-slot:item.PAYMENT_DATE="{ item }">
                <div>
                  <div v-if="item.PAYMENT_DATE" class="font-weight-medium">{{ formatDateOnly(item.PAYMENT_DATE) }}</div>
                  <div v-else class="text--secondary font-italic">Not specified</div>
                </div>
              </template>

              <template v-slot:item.TOTAL_AMOUNT="{ item }">
                <div>
                  <div v-if="item.TOTAL_AMOUNT" class="font-weight-medium">${{ formatAmount(item.TOTAL_AMOUNT) }}</div>
                  <div v-else class="text--secondary font-italic">Not specified</div>
                </div>
              </template>

              <template v-slot:item.UPLOAD_DATE="{ item }">
                <div>
                  <div>{{ formatDateOnly(item.UPLOAD_DATE) }}</div>
                </div>
              </template>

              <template v-slot:item.STATUS="{ item }">
                <v-chip 
                  :color="getStatusColor(item.STATUS)" 
                  small
                >
                  {{ item.STATUS }}
                </v-chip>
              </template>

              <template v-slot:item.DOWNLOAD_COUNT="{ item }">
                <div class="text-center">
                  <div>{{ item.DOWNLOAD_COUNT }}</div>
                  <div v-if="item.LAST_DOWNLOADED" class="caption text--secondary">
                    Last: {{ formatDate(item.LAST_DOWNLOADED) }}
                  </div>
                </div>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="downloadEOP(item)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon>mdi-download</v-icon>
                    </v-btn>
                  </template>
                  <span>Download</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="info"
                      @click="viewEOP(item)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>View Details</span>
                </v-tooltip>

                <v-tooltip bottom v-if="isSuperuser">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="warning"
                      @click="editEOP(item)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit</span>
                </v-tooltip>

                <v-tooltip bottom v-if="isSuperuser">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="error"
                      @click="deleteEOP(item)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </template>
            </v-data-table>

            <!-- Pagination -->
            <v-pagination
              v-if="pagination.pages > 1"
              v-model="currentPage"
              :length="pagination.pages"
              :total-visible="7"
              @input="loadEOPs"
              class="mt-4"
            ></v-pagination>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Upload Dialog -->
    <v-dialog v-model="uploadDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ uploadStep === 'upload' ? 'Upload EOP Document' : 'Review & Confirm Data' }}</span>
        </v-card-title>
        
        <v-card-text>
          <!-- Step 1: Upload File -->
          <div v-if="uploadStep === 'upload'">
            <v-form ref="uploadForm" v-model="uploadFormValid">
              <v-row>
                <v-col cols="12">
                  <v-file-input
                    v-model="uploadFile"
                    label="Select EOP Document"
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
                    show-size
                    :rules="fileRules"
                    required
                  ></v-file-input>
                </v-col>
              </v-row>
            </v-form>
          </div>

          <!-- Step 2: Review Extracted Data -->
          <div v-if="uploadStep === 'review'">
            <!-- Extraction Results -->
            <v-alert
              :type="extractionInfo && extractionInfo.success ? 'success' : 'warning'"
              text
              outlined
              class="mb-4"
            >
              <div class="d-flex align-center">
                <v-icon class="mr-2">{{ extractionInfo && extractionInfo.success ? 'mdi-check-circle' : 'mdi-alert' }}</v-icon>
                <div>
                  <strong>{{ extractionInfo && extractionInfo.success ? 'Data extracted successfully!' : 'Manual input required' }}</strong>
                  <div v-if="extractionInfo && extractionInfo.success" class="caption mt-1">
                    Confidence: {{ extractionInfo.confidence }}%
                  </div>
                </div>
              </div>
            </v-alert>

            <!-- Review Form -->
            <v-form ref="reviewForm" v-model="reviewFormValid">
              <v-row>
                <v-col cols="12">
                  <v-autocomplete
                    v-model="selectedProvider"
                    :items="providerOptions"
                    :loading="providerLoading"
                    :search-input.sync="providerSearch"
                    item-text="displayText"
                    return-object
                    label="Provider"
                    placeholder="Search provider by name or NPI..."
                    clearable
                    @update:search-input="searchProviders"
                    @input="onProviderSelected"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.NAME }}</v-list-item-title>
                        <v-list-item-subtitle>NPI: {{ item.NPI }} - Status: {{ item.STATUS }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="uploadData.DESCRIPTION"
                    label="Description"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="uploadData.TOTAL_AMOUNT"
                    label="Total Amount"
                    type="number"
                    step="0.01"
                    prefix="$"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="uploadData.PAYMENT_DATE"
                    label="Payment Date"
                    type="date"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeUploadDialog">Cancel</v-btn>
          
          <!-- Step 1 Button -->
          <v-btn 
            v-if="uploadStep === 'upload'"
            color="primary" 
            @click="processUpload"
            :loading="uploading"
            :disabled="!uploadFormValid || !uploadFile"
          >
            Upload & Extract Data
          </v-btn>

          <!-- Step 2 Buttons -->
          <template v-if="uploadStep === 'review'">
            <v-btn text @click="uploadStep = 'upload'">Back</v-btn>
            <v-btn 
              color="primary" 
              @click="saveEOP"
              :loading="saving"
              :disabled="!reviewFormValid"
            >
              Save EOP
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View/Edit Dialog -->
    <v-dialog v-model="viewDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">EOP Details</span>
        </v-card-title>
        
        <v-card-text>
          <v-row v-if="selectedEOP">
            <v-col cols="12">
              <v-list>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>EOP ID</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedEOP.EOP_ID }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Provider</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedEOP.PROVIDER_NAME }} (NPI: {{ selectedEOP.PROVIDER_NPI }})</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>File</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedEOP.ORIGINAL_FILE_NAME }} ({{ formatFileSize(selectedEOP.FILE_SIZE) }})</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Upload Date</v-list-item-title>
                    <v-list-item-subtitle>{{ formatDate(selectedEOP.UPLOAD_DATE) }} by {{ selectedEOP.UPLOADED_BY }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item v-if="selectedEOP.DESCRIPTION">
                  <v-list-item-content>
                    <v-list-item-title>Description</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedEOP.DESCRIPTION }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item v-if="selectedEOP.TOTAL_AMOUNT">
                  <v-list-item-content>
                    <v-list-item-title>Total Amount</v-list-item-title>
                    <v-list-item-subtitle>${{ selectedEOP.TOTAL_AMOUNT.toFixed(2) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item v-if="selectedEOP.PAYMENT_DATE">
                  <v-list-item-content>
                    <v-list-item-title>Payment Date</v-list-item-title>
                    <v-list-item-subtitle>{{ formatDateOnly(selectedEOP.PAYMENT_DATE) }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Downloads</v-list-item-title>
                    <v-list-item-subtitle>{{ selectedEOP.DOWNLOAD_COUNT }} times</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item v-if="selectedEOP.TAGS && selectedEOP.TAGS.length > 0">
                  <v-list-item-content>
                    <v-list-item-title>Tags</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-chip 
                        v-for="tag in selectedEOP.TAGS" 
                        :key="tag" 
                        small 
                        class="mr-1"
                      >
                        {{ tag }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="viewDialog = false">Close</v-btn>
          <v-btn color="primary" @click="downloadEOP(selectedEOP)">Download</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="800px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Edit EOP</span>
        </v-card-title>
        
        <v-card-text>
          <v-form ref="editForm" v-model="editFormValid">
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="editData.DESCRIPTION"
                  label="Description"
                  outlined
                  rows="3"
                ></v-textarea>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editData.PAYMENT_DATE"
                  label="Payment Date"
                  type="date"
                  outlined
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editData.TOTAL_AMOUNT"
                  label="Total Amount"
                  type="number"
                  step="0.01"
                  prefix="$"
                  outlined
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-select
                  v-model="editData.STATUS"
                  :items="['Active', 'Inactive', 'Deleted']"
                  label="Status"
                  outlined
                ></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeEditDialog">Cancel</v-btn>
          <v-btn 
            color="primary" 
            @click="saveEOPEdit"
            :disabled="!editFormValid"
          >
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success/Error Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ExplanationOfPayment',
  data() {
    return {
      user: null,
      loading: false,
      eops: [],
      selectedProvider: null,
      providerOptions: [],
      providerLoading: false,
      providerSearch: '',
      selectedEOP: null,
      stats: null,
      
      // Search and filters
      searchQuery: '',
      statusFilter: 'Active',
      sortBy: 'PAYMENT_DATE',
      sortDesc: true,
      currentPage: 1,
      
      // Dialog states
      uploadDialog: false,
      viewDialog: false,
      editDialog: false,
      uploadStep: 'upload', // 'upload' or 'review'
      
      // Upload form
      uploadFormValid: false,
      reviewFormValid: false,
      uploadFile: null,
      extractionInfo: null,
      uploading: false,
      saving: false,
      uploadData: {
        DESCRIPTION: '',
        PAYMENT_DATE: '',
        TOTAL_AMOUNT: '',
        NOTES: '',
        TAGS: ''
      },
      
      // Edit form
      editFormValid: false,
      editData: {
        DESCRIPTION: '',
        PAYMENT_DATE: '',
        TOTAL_AMOUNT: '',
        STATUS: 'Active'
      },
      
         // Table headers
         headers: [
           { text: 'Provider', value: 'PROVIDER_NAME', sortable: true },
           { text: 'File Size', value: 'FILE_SIZE', sortable: true },
           { text: 'Payment Date', value: 'PAYMENT_DATE', sortable: true },
           { text: 'Payment Amount', value: 'TOTAL_AMOUNT', sortable: true },
           { text: 'Upload Date', value: 'UPLOAD_DATE', sortable: true },
           { text: 'Status', value: 'STATUS', sortable: true },
           { text: 'Downloads', value: 'DOWNLOAD_COUNT', sortable: true },
           { text: 'Actions', value: 'actions', sortable: false, width: '200px' }
         ],
      
      // Options
      statusOptions: [
        { text: 'All', value: '' },
        { text: 'Active', value: 'Active' },
        { text: 'Archived', value: 'Archived' },
        { text: 'Deleted', value: 'Deleted' }
      ],
      
         sortOptions: [
           { text: 'Payment Date', value: 'PAYMENT_DATE' },
           { text: 'Upload Date', value: 'UPLOAD_DATE' },
           { text: 'Provider Name', value: 'PROVIDER_NAME' },
           { text: 'File Size', value: 'FILE_SIZE' },
           { text: 'Download Count', value: 'DOWNLOAD_COUNT' }
         ],
      
      // File validation rules
      fileRules: [
        v => !!v || 'File is required',
        v => !v || v.size <= 50 * 1024 * 1024 || 'File size must be less than 50MB',
        v => !v || this.isValidFileType(v) || 'Invalid file type'
      ],
      
      // Pagination
      pagination: {
        page: 1,
        limit: 50,
        total: 0,
        pages: 0
      },
      
      // Snackbar
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      
      // Debounce timer
      searchTimeout: null
    };
  },
  
  computed: {
    isSuperuser() {
      // Only Superuser can perform CRUD operations
      return this.user && this.user.role === 'Superuser';
    },
    canViewAllEOPs() {
      // Admin, Superuser, Provider, and Affiliation can view EOPs
      return this.user && (this.user.role === 'Superuser' || this.user.role === 'Admin' || this.user.role === 'Provider' || this.user.role === 'Affiliation');
    }
  },
  
  mounted() {
    console.log('🚀 EOP Component mounted');
    this.getUser();
    
    // Wait for DOM to be ready
    this.$nextTick(() => {
      console.log('🔄 DOM ready');
      
      // Try to load data regardless of user status
      console.log('🔄 Attempting to load data...');
      
      // Load stats first
      this.loadStats().then(() => {
        console.log('📊 Stats loaded successfully');
      }).catch(error => {
        console.error('❌ Error loading stats:', error);
      });
      
      // Load EOPs second
      this.loadEOPs().then(() => {
        console.log('📋 EOPs loaded successfully');
      }).catch(error => {
        console.error('❌ Error loading EOPs:', error);
      });
    });
  },
  
  methods: {
    async loadEOPs() {
      console.log('🔄 Loading EOPs...');
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          limit: this.pagination.limit,
          sort_by: this.sortBy,
          sort_order: this.sortDesc ? 'desc' : 'asc'
        };
        
        if (this.statusFilter) {
          params.status = this.statusFilter;
        }
        
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }
        
        console.log('📡 API call params:', params);
        const response = await axios.get('/api/eop', { params });
        console.log('✅ EOPs loaded:', response.data);
        this.eops = response.data.eops;
        this.pagination = response.data.pagination;
      } catch (error) {
        console.error('❌ Error loading EOPs:', error);
        this.showSnackbar('Error loading EOPs', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    async loadStats() {
      try {
        const response = await axios.get('/api/eop/stats/overview');
        this.stats = response.data;
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    },
    
    async searchProviders(searchTerm) {
      if (!searchTerm || searchTerm.length < 2) {
        this.providerOptions = [];
        return;
      }

      console.log('🔍 searchProviders called with:', searchTerm);
      this.providerLoading = true;
      try {
        const response = await axios.get(`/api/search/providers/${encodeURIComponent(searchTerm)}`);
        console.log('🔍 Raw API response:', response.data);
        
        // Only update if we have results or if it's a new search (not a duplicate)
        if (response.data && response.data.length > 0) {
          this.providerOptions = response.data.map(provider => ({
            ...provider,
            displayText: `${provider.NAME} (NPI: ${provider.NPI})`
          }));
          console.log('🔍 Processed providerOptions:', this.providerOptions);
        } else if (this.providerOptions.length === 0) {
          // Only clear if we don't have any options yet
          this.providerOptions = [];
        }
      } catch (error) {
        console.error('Error searching providers:', error);
        this.providerOptions = [];
        this.showSnackbar('Error searching providers', 'error');
      } finally {
        this.providerLoading = false;
      }
    },

    onProviderSelected(provider) {
      if (provider) {
        this.showSnackbar(`Provider selected: ${provider.NAME}`, 'success');
      }
    },

    openUploadDialog() {
      this.uploadDialog = true;
      this.resetUploadForm();
    },
    
    closeUploadDialog() {
      this.uploadDialog = false;
      this.resetUploadForm();
    },
    
    resetUploadForm() {
      this.uploadStep = 'upload';
      this.uploadFile = null;
      this.selectedProvider = null;
      this.providerOptions = [];
      this.providerSearch = '';
      this.extractionInfo = null;
      this.uploading = false;
      this.saving = false;
      this.uploadData = {
        DESCRIPTION: '',
        PAYMENT_DATE: '',
        TOTAL_AMOUNT: '',
        NOTES: '',
        TAGS: ''
      };
      if (this.$refs.uploadForm) {
        this.$refs.uploadForm.resetValidation();
      }
      if (this.$refs.reviewForm) {
        this.$refs.reviewForm.resetValidation();
      }
    },
    
    async processUpload() {
      if (!this.$refs.uploadForm.validate()) {
        return;
      }
      
      this.uploading = true;
      try {
        const formData = new FormData();
        formData.append('file', this.uploadFile);
        
        // Only send file for extraction, don't save yet
        const response = await axios.post('/api/eop/extract', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Store extraction info
        if (response.data && response.data.extractionInfo) {
          this.extractionInfo = response.data.extractionInfo;
          
          // Auto-populate form with extracted data
          if (this.extractionInfo.success) {
            // Set provider if found
            if (this.extractionInfo.providerNumber) {
              console.log('🔍 Searching for provider with NPI:', this.extractionInfo.providerNumber);
              // Search for the provider in the database
              await this.searchProviders(this.extractionInfo.providerNumber);
              console.log('🔍 Provider search results:', this.providerOptions);
              if (this.providerOptions.length > 0) {
                this.selectedProvider = this.providerOptions[0];
                console.log('✅ Provider selected:', this.selectedProvider);
                console.log('✅ Selected provider displayText:', this.selectedProvider.displayText);
                console.log('✅ Selected provider NAME:', this.selectedProvider.NAME);
                console.log('✅ Selected provider NPI:', this.selectedProvider.NPI);
                
                // Force Vue reactivity update
                this.$forceUpdate();
              } else {
                console.log('❌ No providers found in search results');
              }
            }
            
            // Set extracted values
            if (this.extractionInfo.paymentAmount) {
              this.uploadData.TOTAL_AMOUNT = this.extractionInfo.paymentAmount;
            }
            
            if (this.extractionInfo.paymentDate) {
              const date = new Date(this.extractionInfo.paymentDate);
              this.uploadData.PAYMENT_DATE = date.toISOString().split('T')[0];
            }
          }
          
          // Move to review step
          this.uploadStep = 'review';
          this.showSnackbar('Data extracted successfully! Please review and confirm.', 'success');
        } else {
          this.showSnackbar('No data could be extracted from the file', 'warning');
          this.uploadStep = 'review';
        }
      } catch (error) {
        console.error('Error processing upload:', error);
        this.showSnackbar('Error processing file', 'error');
      } finally {
        this.uploading = false;
      }
    },
    
    async saveEOP() {
      if (!this.$refs.reviewForm.validate()) {
        return;
      }
      
      this.saving = true;
      try {
        const formData = new FormData();
        formData.append('file', this.uploadFile);
        formData.append('PROVIDER_ID', this.selectedProvider ? this.selectedProvider.NPI : '');
        formData.append('PROVIDER_NAME', this.selectedProvider ? this.selectedProvider.NAME : '');
        formData.append('PROVIDER_NPI', this.selectedProvider ? this.selectedProvider.NPI : '');
        
        // Add other form data
        Object.keys(this.uploadData).forEach(key => {
          if (this.uploadData[key]) {
            formData.append(key, this.uploadData[key]);
          }
        });
        
        const response = await axios.post('/api/eop/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        this.showSnackbar('EOP saved successfully!', 'success');
        this.closeUploadDialog();
        this.loadEOPs();
        this.loadStats();
      } catch (error) {
        console.error('Error saving EOP:', error);
        this.showSnackbar('Error saving EOP', 'error');
      } finally {
        this.saving = false;
      }
    },
    
    async downloadEOP(eop) {
      try {
        const response = await axios.get(`/api/eop/${eop.EOP_ID}/download`);
        
        // Open download URL in new tab
        window.open(response.data.downloadUrl, '_blank');
        
        this.showSnackbar('Download started', 'success');
        this.loadEOPs(); // Refresh to update download count
      } catch (error) {
        console.error('Error downloading EOP:', error);
        this.showSnackbar('Error downloading EOP', 'error');
      }
    },
    
    viewEOP(eop) {
      this.selectedEOP = eop;
      this.viewDialog = true;
    },
    
    editEOP(eop) {
      this.selectedEOP = eop;
      
      // Populate edit form with current data
      this.editData = {
        DESCRIPTION: eop.DESCRIPTION || '',
        PAYMENT_DATE: eop.PAYMENT_DATE ? this.formatDateForInput(eop.PAYMENT_DATE) : '',
        TOTAL_AMOUNT: eop.TOTAL_AMOUNT || '',
        STATUS: eop.STATUS || 'Active'
      };
      
      this.editDialog = true;
    },
    
    async saveEOPEdit() {
      try {
        const updateData = {
          DESCRIPTION: this.editData.DESCRIPTION,
          PAYMENT_DATE: this.editData.PAYMENT_DATE,
          TOTAL_AMOUNT: this.editData.TOTAL_AMOUNT,
          STATUS: this.editData.STATUS
        };
        
        await axios.put(`/api/eop/${this.selectedEOP.EOP_ID}`, updateData);
        
        this.showSnackbar('EOP updated successfully', 'success');
        this.closeEditDialog();
        await this.loadEOPs();
        await this.loadStats();
      } catch (error) {
        console.error('Error updating EOP:', error);
        this.showSnackbar('Error updating EOP', 'error');
      }
    },
    
    closeEditDialog() {
      this.editDialog = false;
      this.selectedEOP = null;
      this.editData = {
        DESCRIPTION: '',
        PAYMENT_DATE: '',
        TOTAL_AMOUNT: '',
        STATUS: 'Active'
      };
    },
    
    async deleteEOP(eop) {
      if (confirm(`Are you sure you want to delete this EOP?`)) {
        try {
          await axios.delete(`/api/eop/${eop.EOP_ID}`);
          this.showSnackbar('EOP deleted successfully', 'success');
          this.loadEOPs();
          this.loadStats();
        } catch (error) {
          console.error('Error deleting EOP:', error);
          this.showSnackbar('Error deleting EOP', 'error');
        }
      }
    },
    
    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadEOPs();
      }, 500);
    },
    
    isValidFileType(file) {
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/gif',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      return allowedTypes.includes(file.type);
    },
    
    getStatusColor(status) {
      const colors = {
        'Active': 'success',
        'Archived': 'warning',
        'Deleted': 'error'
      };
      return colors[status] || 'default';
    },
    
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateOnly(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    formatDateForInput(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toISOString().split('T')[0];
    },

    formatAmount(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toFixed(2);
    },
    
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    showSnackbar(message, color = 'success') {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    
    async getUser() {
      try {
        const response = await axios.get('/api/user');
        if (response.data && response.data.length > 0) {
          this.user = response.data[0];
          console.log('👤 User loaded in EOP:', this.user);
        }
      } catch (error) {
        console.log('⚠️ User not authenticated in EOP');
        this.user = null;
      }
    }
  }
};
</script>

<style scoped>
.text-h4 {
  color: #1976d2;
}

.v-card {
  border-radius: 8px;
}

.v-data-table {
  border-radius: 8px;
}

.caption {
  font-size: 0.75rem;
}
</style>
